import React, { useEffect, useMemo, useState } from "react";
import { BiInfoCircle } from "react-icons/bi";
import { Assort } from "../../../models/Assort";
import "./style.scss";
import "../../../../style/block.scss";
import "../../../../style/feature.scss";
import "../../../../style/hovers.scss";
import { TicketAccess } from "../../../models/TicketAccess";
import "../../../../style/buttons.scss";
import getId from "../../../../utils/getId";
import { BasketItem } from "../../../models/BasketItem";
import { useTranslation } from "react-i18next";
import { QntStateItem } from "../../../models/QntStateItem";
import EAQuantitySelector from "../../shared/QuantitySelector/EAQuantitySelector";
import EAChip from "../../shared/Chip/EAChip";
import EAModal from "../../shared/Modal/EAModal";
import { stripHtml } from "../../../../utils/stripHtml";

interface IProps {
  ticketLimit?: number;
  item: Assort;
  exercise?: TicketAccess;
  preBasket: BasketItem[];
  addItemToBasket: (assort: Assort, exercise?: TicketAccess) => void;
  removeItemFromBasket: (assort: Assort, exercise?: TicketAccess) => void;
  setItemQuantity?: (assort: Assort, newQuantity: number, exercise?: TicketAccess) => void;
  qntState?: QntStateItem[];
  allowManualInput?: boolean;
  eventCapacityLimit?: number;
  assortSpecificLimit?: number | null; // limit specyficzny dla tego biletu (remainingQuantity z API)
  /** "roomy" = wiersz o większych odstępach z wypełnieniem wybranego biletu (sprzedaż kalendarzowa) */
  variant?: "default" | "roomy";
} // optional for styling

const formatAmount = (val: number): string => {
  return Number.isInteger(val) ? val.toString() : val.toFixed(2);
};

const TicketLineSummary: React.FC<IProps> = ({
  item,
  exercise,
  preBasket,
  ticketLimit,
  addItemToBasket,
  removeItemFromBasket,
  setItemQuantity,
  qntState,
  allowManualInput = false,
  eventCapacityLimit,
  assortSpecificLimit,
  variant = "default",
}: IProps) => {
  const { t } = useTranslation();

  const getQnt = (value: Assort): number => {
    let result = 0;
    if (qntState && qntState.length > 0 && exercise) {
      const foundQnt = qntState.find(
        (item) => item.assortIds === getId(value) && item.exerciseIds === exercise.exerciseIds,
      );
      result = foundQnt ? foundQnt.count : 0;
    } else {
      const itemBasket = preBasket.find((item) => item.assortIds === getId(value));
      result = itemBasket ? itemBasket.qnt : 0;
    }

    return result;
  };

  useEffect(() => {
    if (item.maxSale === 0 || !item.maxSale) {
      setMaxQnt(99);
    } else {
      setMaxQnt(item.maxSale);
    }
    //eslint-disable-next-line
  }, []);
  const [open, setOpen] = useState(false);
  const [maxQnt, setMaxQnt] = useState(0);
  const isDiscount = item.price !== item.listPrice && item.listPrice > item.price;

  const descriptionPlain = useMemo(() => stripHtml(item.description || ""), [item.description]);
  const hasDescription = descriptionPlain.length > 0;

  const priceCaption = useMemo(() => {
    if (isDiscount) {
      const diff = item.listPrice - item.price;
      return `-${formatAmount(diff)} ${t("zl")}`;
    }
    const unitName = item.unitKey?.displayName?.trim();
    if (unitName && !["szt", "szt.", "sztuka"].includes(unitName.toLowerCase())) {
      return unitName;
    }
    return "";
  }, [isDiscount, item.listPrice, item.price, item.unitKey, t]);

  const ticketsInCurrentBasket = () => {
    if (qntState && qntState.length > 0 && exercise) {
      return qntState
        .filter((item) => item.exerciseIds === exercise.exerciseIds)
        .reduce((acc: number, item: QntStateItem) => acc + item.count, 0);
    } else {
      return preBasket.reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);
    }
  };

  const handleQuantityChange = (newValue: number) => {
    if (!allowManualInput) return;

    if (setItemQuantity) {
      // Użyj bezpośredniego ustawienia ilości jeśli dostępne
      setItemQuantity(item, newValue, exercise);
    } else {
      // Fallback do starej logiki z pętlą (dla kompatybilności wstecznej)
      const currentQnt = getQnt(item);
      const diff = newValue - currentQnt;

      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          addItemToBasket(item, exercise);
        }
      } else if (diff < 0) {
        for (let i = 0; i < Math.abs(diff); i++) {
          removeItemFromBasket(item, exercise);
        }
      }
    }
  };

  const getEffectiveMax = () => {
    let effectiveMax = maxQnt;
    const currentQnt = getQnt(item);

    // 1) Limit specyficzny dla asortymentu (np. TourSale) – jeśli istnieje, zawsze zawęża max
    //    UWAGA: null = brak limitu, używamy maxQnt
    if (assortSpecificLimit !== undefined && assortSpecificLimit !== null) {
      effectiveMax = Math.min(effectiveMax, assortSpecificLimit);
    }

    // 2) ticketLimit (globalny limit na wydarzenie / komponent) – działa równolegle
    if (ticketLimit !== undefined) {
      const remainingTickets = ticketLimit - ticketsInCurrentBasket() + currentQnt;
      effectiveMax = Math.min(effectiveMax, remainingTickets);
    }

    // 3) eventCapacityLimit (globalna pojemność wydarzenia, np. capacity - reserved lub globalLimit)
    if (eventCapacityLimit !== undefined) {
      const remainingEventCapacity = eventCapacityLimit - ticketsInCurrentBasket() + currentQnt;
      effectiveMax = Math.min(effectiveMax, remainingEventCapacity);
    }

    return Math.max(0, effectiveMax); // Nie może być ujemne
  };

  const effectiveMax = getEffectiveMax();

  const itemName = item.displayWebName || item.name;

  return (
    <>
      <div
        className={[
          "ticket-line-summary",
          getQnt(item) > 0 ? "ticket-line-active" : "",
          "ticket-line flex-between-center",
          variant === "roomy" ? "ticket-line--roomy" : "",
          effectiveMax === 0 ? "ticket-line--unavailable" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="ticket-line-left">
          <div className="ticket-line-name-block">
            {hasDescription ? (
              <b className="ticket-line-name" title={itemName}>
                {itemName}
              </b>
            ) : (
              <button type="button" className="ticket-line-name ticket-line-name-button" title={itemName} onClick={() => setOpen(true)}>
                {itemName}
              </button>
            )}

            {hasDescription && (
              <button type="button" className="ticket-line-description-button" onClick={() => setOpen(true)}>
                <span className="ticket-line-description">{descriptionPlain}</span>
                <BiInfoCircle className="ticket-line-description-more" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="ticket-line-right flex-end-center">
          {effectiveMax === 0 && (
            <span className="ticket-line-unavailable-row">
              <EAChip label={t("niedostepne")} variant="dark" className="ticket-line__unavailable-chip" />
            </span>
          )}
          <div className="price-container">
            <div className="price-row">
              {isDiscount && (
                <span className="price price-through">
                  {formatAmount(item.listPrice)} {t("zl")}
                </span>
              )}
              <b className="price">
                {formatAmount(item.price)} {t("zl")}
              </b>
            </div>
            {priceCaption && <span className="price-caption">{priceCaption}</span>}
          </div>

          <div className="qnt flex-end-center">
            <EAQuantitySelector
              value={getQnt(item)}
              onChange={handleQuantityChange}
              onIncrement={() => addItemToBasket(item, exercise)}
              onDecrement={() => removeItemFromBasket(item, exercise)}
              disabled={!allowManualInput}
              incrementDisabled={getQnt(item) >= effectiveMax}
              decrementDisabled={getQnt(item) <= 0}
              min={0}
              max={effectiveMax}
              showMaxInfo={
                (assortSpecificLimit !== undefined && assortSpecificLimit !== null && assortSpecificLimit > 0) ||
                (eventCapacityLimit !== undefined && eventCapacityLimit > 0 && eventCapacityLimit < maxQnt)
              }
              className="quantity-selector--end"
            />
          </div>
        </div>
      </div>

      <EAModal
        isOpen={open}
        title={""}
        onClose={() => setOpen(false)}
        cancelButtonLabel={t("przeczytalem")}
        hideAcceptButton
      >
        <p className="ticket-line-modal-name">{itemName}</p>
        {item.description.length > 1 ? (
          <div className="ticket-line-modal-description" dangerouslySetInnerHTML={{ __html: item.description }} />
        ) : (
          <p>{t("brak_opisu")}</p>
        )}
      </EAModal>
    </>
  );
};

export default TicketLineSummary;
